/**
 * 深色模式（跟随 App 外观）
 *
 * App 原生殼怎麼傳外觀（任一即可，優先順序由上到下）：
 * 1. 文檔開始前注入 `window.__NICEE_THEME__ = 'dark' | 'light' | 'system'`
 *    （iOS WKUserScript / Android WebViewCompat.addDocumentStartJavaScript）
 * 2. Android JsInterface 同步方法 `window.Android.getAppTheme()`
 * 3. URL 參數 `theme=dark|light|system`（search 或 hash 裡的 query 都認）
 * 4. 同一個 WebView 會話之前收到過的值（sessionStorage）
 * 外觀切換時原生呼叫 `window.niceeSetTheme('dark')` 即時更新。
 *
 * 沒收到任何值時：App 內（舊版 App）保持淺色；一般瀏覽器跟隨系統 prefers-color-scheme。
 *
 * 只有已適配深色的頁面（DARK_READY_PATHS）才會真的切成深色：
 * `<html data-theme="dark">`，樣式見 styles/theme.scss；其它頁面維持 `data-theme="light"`，外觀不變。
 */
import Vue from 'vue';
import util from './util';

export const THEME_LIGHT = 'light';
export const THEME_DARK = 'dark';
export const THEME_SYSTEM = 'system';

const VALID_THEMES = [THEME_LIGHT, THEME_DARK, THEME_SYSTEM];
const STORAGE_KEY = 'NICEE_APP_THEME';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** 已適配深色的頁面（路由 path 精確匹配）；新頁面適配完加到這裡 */
export const DARK_READY_PATHS = Object.freeze([
  '/income', // 賬單：可提領 / 結算中 / 收支明細
  '/bill', // 歷史賬單：nicee 幣 / 鑽石 / 糖果
  '/rules',
  '/privacy'
]);

export const themeState = Vue.observable({
  preference: THEME_LIGHT,
  systemDark: false,
  pageReady: false
});

function normalizeTheme(value) {
  if (typeof value !== 'string') return '';
  const theme = value.trim().toLowerCase();
  return VALID_THEMES.includes(theme) ? theme : '';
}

/** 從 search 與 hash 兩段 query 裡讀參數（util.getUrlParam 是子串匹配，不可靠） */
function readUrlParam(key, href) {
  const url = href || window.location.href;
  const queries = url.split(/[?#]/).slice(1);
  for (let i = queries.length - 1; i >= 0; i -= 1) {
    const pairs = queries[i].split('&');
    for (let j = 0; j < pairs.length; j += 1) {
      const [rawKey, rawValue = ''] = pairs[j].split('=');
      if (rawKey === key) {
        try {
          return decodeURIComponent(rawValue);
        } catch (e) {
          return rawValue;
        }
      }
    }
  }
  return '';
}

function readStorage() {
  try {
    return normalizeTheme(window.sessionStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return '';
  }
}

function writeStorage(theme) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    // 隱私模式下 sessionStorage 可能不可用，只影響跨頁記憶
  }
}

function readAndroidTheme() {
  try {
    const bridge = window.Android;
    if (bridge && typeof bridge.getAppTheme === 'function') {
      return normalizeTheme(bridge.getAppTheme());
    }
  } catch (e) {
    console.warn('[theme] Android.getAppTheme failed', e);
  }
  return '';
}

function readExplicitTheme(href) {
  return (
    normalizeTheme(window.__NICEE_THEME__) ||
    readAndroidTheme() ||
    normalizeTheme(readUrlParam('theme', href)) ||
    readStorage()
  );
}

function defaultTheme() {
  return util.isInAppWebview ? THEME_LIGHT : THEME_SYSTEM;
}

export function isDarkTheme() {
  const { preference, systemDark } = themeState;
  return preference === THEME_DARK || (preference === THEME_SYSTEM && systemDark);
}

/** 當前頁面是否正以深色顯示（組件裡用這個決定 inline 顏色） */
export function isPageDark() {
  return themeState.pageReady && isDarkTheme();
}

export function isDarkReadyPath(path) {
  return DARK_READY_PATHS.includes(path);
}

function applyToDocument() {
  const root = document.documentElement;
  if (!root) return;
  root.setAttribute('data-theme', isPageDark() ? THEME_DARK : THEME_LIGHT);
}

export function setThemePreference(value) {
  const theme = normalizeTheme(value);
  if (!theme) {
    console.warn('[theme] ignore invalid theme', value);
    return false;
  }
  // 以後路由切換也以這個值為準（URL 裡可能還帶著打開頁面時的舊 theme 參數）
  window.__NICEE_THEME__ = theme;
  themeState.preference = theme;
  writeStorage(theme);
  applyToDocument();
  return true;
}

/** 路由切換後呼叫：決定當前頁面要不要用深色 */
export function updateThemeForRoute(route) {
  const explicit = readExplicitTheme();
  if (explicit && explicit !== themeState.preference) {
    themeState.preference = explicit;
    writeStorage(explicit);
  }
  themeState.pageReady = isDarkReadyPath(route ? route.path : '');
  applyToDocument();
}

function currentHashPath() {
  const hash = window.location.hash || '';
  const path = hash.replace(/^#/, '').split('?')[0];
  return path || '/';
}

function watchSystemTheme() {
  if (!window.matchMedia) return;
  const media = window.matchMedia(DARK_QUERY);
  themeState.systemDark = !!media.matches;
  const onChange = event => {
    themeState.systemDark = !!event.matches;
    applyToDocument();
  };
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', onChange);
  } else if (typeof media.addListener === 'function') {
    media.addListener(onChange);
  }
}

/** main.js 最早呼叫：首屏就套上主題，避免白閃 */
export function initTheme() {
  watchSystemTheme();
  themeState.preference = readExplicitTheme() || defaultTheme();
  themeState.pageReady = isDarkReadyPath(currentHashPath());
  applyToDocument();
  // 原生外觀切換時呼叫：window.niceeSetTheme('dark' | 'light' | 'system')
  window.niceeSetTheme = setThemePreference;
}
